import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from 'react-router-dom';
import { trimText } from "shared/utils/utils";
import { BREADCRUMB_DYNAMIC_CONFIG, BREADCRUMB_TYPE_CONFIG, HOME } from "./config";
import { LeftArrow } from "shared/assets/icons";

import './Breadcrumbs.scss';

export const resetBreadcrumbs = () => {
    sessionStorage.setItem("breadcrumbs", JSON.stringify([HOME]));
};

const setSessionBreadcrumbs = (data) => {
    sessionStorage.setItem("breadcrumbs", JSON.stringify(data));
};

const getSessionBreadcrumbs = () => {
    const sessionBreadcrumbs = sessionStorage.getItem("breadcrumbs");
    if ( typeof sessionBreadcrumbs === 'string' ) {
        return JSON.parse(sessionBreadcrumbs);
    } else {
        return [HOME];
    }
};

const findPathIndex = (breadcrumbs, url, partialMatchPath=null) => {
    const index = breadcrumbs.findIndex(elem => {
        if ( elem.path === url ) {
            return true;
        } else if ( Array.isArray(elem.addOnBreadcrumbs) && elem.addOnBreadcrumbs.length > 0 && ( partialMatchPath ? elem.addOnBreadcrumbs[0].path.startsWith(partialMatchPath) : elem.addOnBreadcrumbs[0].path === url ) ) {
            return true;
        }

        return false;
    });

    return index;
};

export const navigateUsingBreadcrumbs = (history, url, partialMatchPath=null) => {
    const sessionBreadcrumbs = getSessionBreadcrumbs();
    const index = findPathIndex(sessionBreadcrumbs, url, partialMatchPath);
    if ( index < 0 ) {
        history.push(url);
    } else {
        const pagesToGoBack = (index - sessionBreadcrumbs.length) + 1;
        if (pagesToGoBack < 0) {
            history.go(pagesToGoBack);

            setSessionBreadcrumbs(sessionBreadcrumbs.slice(0, index + 1));
        }
    }
};

export const BreadcrumbsManager = (props) => {

    const currentPath = useSelector(state => state.currentPath);
    const previousPathRef = useRef("");

    // Block browser back/forward when on home page
    useEffect(() => {
        if (props.location.pathname === '/') {
            // Replace history to prevent going back
            window.history.pushState(null, null, window.location.href);
            
            // Listen for back button attempt
            const handlePopState = (event) => {
                window.history.pushState(null, null, window.location.href);
            };
            
            window.addEventListener('popstate', handlePopState);
            
            return () => {
                window.removeEventListener('popstate', handlePopState);
            };
        }
    }, [props.location.pathname]);

    useLayoutEffect(() => {
        const sessionBreadcrumbs = getSessionBreadcrumbs();
        const previousPathMatchesSecondBreadcrumb = (sessionBreadcrumbs[1]?.path === previousPathRef.current || sessionBreadcrumbs[1]?.addOnBreadcrumbs?.some((subItem) => subItem?.path === previousPathRef.current));
        const previousPathIsCategoryPath = previousPathRef.current?.startsWith('/category');
        const currentPathMatchesPreviousPath = props.location.pathname === previousPathRef.current;

        if (
            props.history.action === "POP" &&
            Array.isArray(sessionBreadcrumbs) &&
            (sessionBreadcrumbs.length === 2 || previousPathRef.current == "/estate-sale/list" || previousPathRef.current == "/locations" || previousPathRef.current == "/watching" || previousPathRef.current == "/my-bids"|| previousPathRef.current == "/explore" || previousPathRef.current.startsWith("/category")) &&
            previousPathRef.current !== '/' &&
            props.location.pathname !== '/' &&
            sessionBreadcrumbs[1]?.path !== props.location.pathname &&
            (previousPathMatchesSecondBreadcrumb || previousPathIsCategoryPath || currentPathMatchesPreviousPath)
        ) {
            resetBreadcrumbs();
            props.history.replace('/');
        }
    }, [props.location.pathname, props.history]);

    useEffect(() => {
        previousPathRef.current = props.location.pathname;
    }, [props.location.pathname]);
    
    useEffect(() => {
        // Scenario - When full page is refreshed.

        // Getting session breadcrumbs
        const sessionBreadcrumbs = getSessionBreadcrumbs();

        // Finding is the current path is already available in session breadcrumbs
        const index = sessionBreadcrumbs.findIndex(elem => elem.path === props.location.pathname);

        // If the current path exist and it is not the last path. Then re-setting the breadcrumbs.
        if ( index > -1 && props.location.pathname !== sessionBreadcrumbs[sessionBreadcrumbs.length - 1].path ) {
            resetBreadcrumbs();
        }
    }, []);

    useEffect(() => {
        // Scenario - When the navigation hits the home page.
        if ( currentPath === '/' ) {
            resetBreadcrumbs();
        }
    }, [currentPath]);

    return null;
};

export const Breadcrumbs = ({ props, type, options, mobileBack=false, style }) => {
    
    const [breadcrumbs, setBreadcrumbs] = useState([]);
    const history = useHistory();

    useEffect(() => {
        if ( type && type in BREADCRUMB_DYNAMIC_CONFIG ) {
            let sessionBreadcrumbs = getSessionBreadcrumbs();
            let shouldPersistSession = true;

            let data = BREADCRUMB_DYNAMIC_CONFIG[type](options);
            if ('addOnBreadcrumbs' in data) {
                data.addOnBreadcrumbs[0].path = '/category';
                data.addOnBreadcrumbs[1].path = `/category/${props.match.params.categoryGroup}`;

                if (data.addOnBreadcrumbs[2]?.title) {
                    data.addOnBreadcrumbs[2].path = `/category/${props.match.params.categoryGroup}/${props.match.params.category}`;
                }
            } else {
                if (type === BREADCRUMB_TYPE_CONFIG.ITEM_VIEW && options) {
                    const expectedItemPath = `/estate-sale/${options.saleId}/item/${options.itemId}`;
                    if (props.location.pathname.startsWith(expectedItemPath)) {
                        data.path = props.location.pathname;
                    } else {
                        const itemSeoUrl = options.itemSeoUrl || options.itemTitle;
                        data.path = itemSeoUrl 
                            ? `/estate-sale/${options.saleId}/item/${options.itemId}/${itemSeoUrl}`
                            : `/estate-sale/${options.saleId}/item/${options.itemId}`;
                    }
                } else {
                    data.path = props.location.pathname;
                }
            }

            const index = findPathIndex(sessionBreadcrumbs, props.location.pathname);

            if ( index < 0 ) {

                // Scenario - This will remove the last breadcrumb.
                if ( props.history.action === "REPLACE" && (sessionBreadcrumbs[sessionBreadcrumbs.length - 1].path !== '/' && sessionBreadcrumbs[sessionBreadcrumbs.length - 1].path !== '') ) {
                    sessionBreadcrumbs.splice(sessionBreadcrumbs.length - 1, 1);
                }

                if ( type === BREADCRUMB_TYPE_CONFIG.CATEGORY && sessionBreadcrumbs.length > 1 ) {
                    const hasCategoryBreadcrumb = sessionBreadcrumbs.some(breadcrumb =>
                        breadcrumb.addOnBreadcrumbs && breadcrumb.addOnBreadcrumbs.some(subItem =>
                            subItem.path && subItem.path.startsWith('/category')
                        )
                    );

                    if ( hasCategoryBreadcrumb ) {
                        sessionBreadcrumbs = [HOME];
                    }
                }

                sessionBreadcrumbs.push(data);
            } else {
                const existingBreadCrumb = sessionBreadcrumbs[index];

                // Refresh stored title/path if current item data has changed.
                if ( existingBreadCrumb && (existingBreadCrumb.title !== data.title || existingBreadCrumb.path !== data.path) ) {
                    sessionBreadcrumbs[index] = {
                        ...existingBreadCrumb,
                        ...data
                    };
                }

                // Back/Forward both are POP in history action.
                // Do not persist trimmed stack on POP, otherwise forward trail gets lost.
                // if ( props.history.action === "POP" ) {
                //     shouldPersistSession = false;
                // }

                // Scenario - If the breadcrumb path is already present, then removing the other breadcrumbs after that.
                sessionBreadcrumbs = sessionBreadcrumbs.slice(0, index + 1);

                // Scenario - Category breadcrumb contains sub-items (Category / Group / SubCategory)
                // and only one of them matches the current path. In this case, trim only to
                // the matched sub-item so breadcrumb UI does not keep showing extra levels.
                if ( Array.isArray(existingBreadCrumb?.addOnBreadcrumbs) && existingBreadCrumb.addOnBreadcrumbs.length > 0 ) {
                    const subIndex = existingBreadCrumb.addOnBreadcrumbs.findIndex((subItem) => subItem?.path === props.location.pathname);

                    if ( subIndex > -1 ) {
                        sessionBreadcrumbs[index] = {
                            ...sessionBreadcrumbs[index],
                            addOnBreadcrumbs: existingBreadCrumb.addOnBreadcrumbs.slice(0, subIndex + 1)
                        };
                    }
                }
            }

            setBreadcrumbs(sessionBreadcrumbs);

            if ( shouldPersistSession ) {
                setSessionBreadcrumbs(sessionBreadcrumbs);
            }
        }
    }, [type, props.location.pathname, options]);

    const onClickLink = (item, index, subIndex=null) => {
        if (item.path === '/') {
            props.history.push('/');
            resetBreadcrumbs();
        } else {
            if ( item.path?.startsWith('/category') && item.path !== props.location.pathname ) {
                const sessionBreadcrumbs = getSessionBreadcrumbs();
                const trimmedBreadcrumbs = sessionBreadcrumbs.slice(0, index + 1);

                if ( subIndex !== null && Array.isArray(trimmedBreadcrumbs[index]?.addOnBreadcrumbs) ) {
                    trimmedBreadcrumbs[index] = {
                        ...trimmedBreadcrumbs[index],
                        addOnBreadcrumbs: trimmedBreadcrumbs[index].addOnBreadcrumbs.slice(0, subIndex + 1)
                    };
                }

                setSessionBreadcrumbs(trimmedBreadcrumbs);
                setBreadcrumbs(trimmedBreadcrumbs);

                const checkCat = subIndex !== null ? subIndex : index;
                props.history.push(item.path, { checkCat });

            } else {
                const pagesToGoBack = (index - breadcrumbs.length) + 1;
                if (pagesToGoBack < 0) {
                    props.history.go(pagesToGoBack);
    
                    const sessionBreadcrumbs = getSessionBreadcrumbs();
                    setSessionBreadcrumbs(sessionBreadcrumbs.slice(0, index + 1));
                }
            }
        }
    };

    const onClickBack = () => {
        props.history.go(-1);
    };

    const renderItem = (item, index) => {
        return (
            <>
                <div key={"breadcrumbs-" + index}  className={`link ${index === breadcrumbs.length - 1 ? 'active' : ''}`} aria-label={item.title} title={item.title} onClick={() => { onClickLink(item, index); }}>{trimText(item.title, 50)}</div>
                { 
                    index != breadcrumbs.length - 1 ? 
                    <div key={"breadcrumbs-slash" + index} className="slash">
                        <svg width="8" height="12" viewBox="0 0 8 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path fill-rule="evenodd" clip-rule="evenodd" d="M7.72088 0.79084C8.02048 1.00485 8.08988 1.42121 7.87587 1.72082L1.20921 11.0542C0.995201 11.3538 0.578835 11.4232 0.279226 11.2092C-0.020382 10.9951 -0.0897765 10.5788 0.124229 10.2792L6.7909 0.945837C7.0049 0.646229 7.42127 0.576834 7.72088 0.79084Z" fill="#868E96"/>
                        </svg>
                    </div>  : ''
                }
            </>
        )
    };

    const renderSubItem = (items, item, index, subIndex) => {
        return (
            <>
                <div key={"breadcrumbs-" + subIndex}  className={`link ${index === breadcrumbs.length - 1 ? 'active' : ''}`} aria-label={item.title} title={item.title} onClick={() => { onClickLink(item, index, subIndex); }}>{trimText(item.title, 50)}</div>
                { 
                    subIndex != items.length - 1 || index != breadcrumbs.length - 1 ? 
                    <div key={"breadcrumbs-slash" + subIndex} className="slash">
                        <svg width="8" height="12" viewBox="0 0 8 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path fill-rule="evenodd" clip-rule="evenodd" d="M7.72088 0.79084C8.02048 1.00485 8.08988 1.42121 7.87587 1.72082L1.20921 11.0542C0.995201 11.3538 0.578835 11.4232 0.279226 11.2092C-0.020382 10.9951 -0.0897765 10.5788 0.124229 10.2792L6.7909 0.945837C7.0049 0.646229 7.42127 0.576834 7.72088 0.79084Z" fill="#868E96"/>
                        </svg>
                    </div>  : ''
                }
            </>
        )
    };
    
    return (
        <div className="breadcrumbs" style={style}>
            {
                breadcrumbs.map((item, index) => 
                    <>
                        {
                            "addOnBreadcrumbs" in item ? 
                            item['addOnBreadcrumbs'].map((subItem, subIndex) => renderSubItem(item['addOnBreadcrumbs'], subItem, index, subIndex)) :
                            renderItem(item, index)
                        }
                    </>
                )
            }
            {
                Array.isArray(breadcrumbs) && breadcrumbs.length > 0 && mobileBack ? 
                <div className="back" onClick={onClickBack}>
                    {LeftArrow}
                    <span>Back</span>
                </div> : null
            }
        </div>
    )
};
